import { TD } from '@/components/DesignLibrary/Table';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useState } from 'react';
import { CustomStatus } from '@/typings/api-models';
import WorkflowOptionSelect from '@/components/WorkflowOptionSelect';
import ArchiveWorkflowConfirmationModal from '@/modals/ArchiveWorkflowConfirmationModal';
import * as format from '@format';

import s from './WorkflowStatus.module.scss';

type SelectOption = Parameters<typeof WorkflowOptionSelect>[0]['currentOption'];

export type Props = {
  customStatuses: CustomStatus[];
  type: 'status';
  userWorkflowId: string;
  isAttorneyCase: boolean;
  currentStatus: SelectOption;
  pausedUntil: string | null;
};

const WorkflowStatus: React.FC<Props> = ({ customStatuses, userWorkflowId, isAttorneyCase, currentStatus, pausedUntil }) => {
  const [currentSetting, setCurrentSetting] = useState<SelectOption>(currentStatus);
  const [isArchiveWorkflowLoading, setIsArchiveWorkflowLoading] = useState(false);
  const [isArchiveWorkflowConfirmationModalOpen, setIsArchiveWorkflowConfirmationModalOpen] = useState(false);

  const { getData: updateUserWorkflow } = useNoodleApi(tsClient.workflows.updateUserWorkflow);

  const handleArchivedWorkflowModalConfirmation = async (): Promise<void> => {
    setIsArchiveWorkflowLoading(true);
    setCurrentSetting('archived');
    await updateUserWorkflow({ updates: { status: 'archived' }, userWorkflowId });
    setIsArchiveWorkflowLoading(false);
    setIsArchiveWorkflowConfirmationModalOpen(false);
  };

  const workflowSettingChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const newStatus = event.target.value;

    if (newStatus === 'archived') {
      setIsArchiveWorkflowConfirmationModalOpen(true);
    } else if (newStatus === 'completed') {
      if (window.confirm('Are you sure you want to complete this workflow?')) {
        setCurrentSetting(newStatus);
        await updateUserWorkflow({ updates: { status: 'completed' }, userWorkflowId });
      }
    } else if (newStatus === 'data-collection') {
      if (pausedUntil) {
        setCurrentSetting(newStatus);
        await updateUserWorkflow({ updates: { pausedUntil: null, status: 'data-collection' }, userWorkflowId });
      } else {
        setCurrentSetting(newStatus);
        await updateUserWorkflow({ updates: { status: 'data-collection' }, userWorkflowId });
      }
    } else {
      setCurrentSetting(newStatus);
      await updateUserWorkflow({ updates: { status: newStatus }, userWorkflowId });
    }
  };

  return (
    <>
      <TD compact className={s.cell}>
        <WorkflowOptionSelect
          statuses={customStatuses}
          currentOption={currentSetting}
          isAttorneyCase={isAttorneyCase}
          onChange={workflowSettingChange}
          className={s.workflowOptionSelect}
        />
        {pausedUntil && <p className='caption'>Paused until {format.datetimeLocalTZ.abbreviated(pausedUntil)}</p>}
      </TD>
      {isArchiveWorkflowConfirmationModalOpen
      && <ArchiveWorkflowConfirmationModal
        onConfirm={handleArchivedWorkflowModalConfirmation}
        setIsArchiveWorkflowConfirmationModalOpen={setIsArchiveWorkflowConfirmationModalOpen}
        loading={isArchiveWorkflowLoading}
      />}
    </>
  );
};

export default WorkflowStatus;
